import axios from 'axios';

// Fallback — относительный путь (см. комментарий в client.ts)
const API_URL = import.meta.env.VITE_API_URL || '/api';

export type ErrorLevel = 'WARNING' | 'ERROR' | 'CRITICAL';

export interface ErrorReport {
  type: string;
  name: string;
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  level?: ErrorLevel;
  context?: Record<string, unknown>;
}

const reportedErrors = new Set<string>();

function getErrorKey(error: ErrorReport): string {
  return `${error.type}:${error.name}:${error.message}`;
}

export async function reportError(error: ErrorReport): Promise<void> {
  const errorKey = getErrorKey(error);

  // Prevent duplicate reports for the same error in the session
  if (reportedErrors.has(errorKey)) {
    return;
  }
  reportedErrors.add(errorKey);

  // Add browser info
  const enrichedError: ErrorReport = {
    ...error,
    url: error.url || window.location.href,
    userAgent: error.userAgent || navigator.userAgent,
    level: error.level || 'ERROR',
  };

  try {
    // Use raw axios to avoid auth interceptors - this endpoint is public
    await axios.post(`${API_URL}/errors/report`, enrichedError, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 5000, // 5 second timeout
    });
  } catch (e) {
    // Silently fail - don't cause more errors from error reporting
    console.warn('Failed to report error to server:', e);
  }
}

export function reportErrorFromCatch(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (error instanceof Error) {
    reportError({
      type: error.constructor.name,
      name: error.name,
      message: error.message,
      stack: error.stack,
      context,
    });
  } else {
    reportError({
      type: 'UnknownError',
      name: 'UnknownError',
      message: String(error),
      context,
    });
  }
}

// Filter out errors originating from browser extensions (MetaMask, etc.)
function isBrowserExtensionError(stack?: string, filename?: string): boolean {
  const extPatterns = ['chrome-extension://', 'moz-extension://', 'safari-extension://'];
  const source = (stack || '') + (filename || '');
  return extPatterns.some((p) => source.includes(p));
}

// Global error handler for unhandled errors
export function setupGlobalErrorHandlers(): void {
  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    if (isBrowserExtensionError(event.error?.stack, event.filename)) return;

    reportError({
      type: 'UncaughtError',
      name: event.error?.name || 'Error',
      message: event.message,
      stack: event.error?.stack,
      level: 'CRITICAL',
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (isBrowserExtensionError(error?.stack)) return;

    reportError({
      type: 'UnhandledPromiseRejection',
      name: error?.name || 'PromiseRejection',
      message: error?.message || String(error),
      stack: error?.stack,
      level: 'CRITICAL',
      context: {
        reason: String(error),
      },
    });
  });
}
